import { Box, Center, Flex, Icon, Text } from '@chakra-ui/react';
import { FaChevronRight } from '@react-icons/all-files/fa/FaChevronRight';
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle';

import { ContactMessageEntry } from '../../../../../../services/marketplace/generated/kubb/models/ContactMessageEntry';
import UnreadCountBubble from '../../../../notifications/UnreadCountBubble';
import MarketplaceCard from '../../MarketplaceCard';
import { MessagePreview, PLATFORM_LABEL, getMessagePreview } from '../conversation/utils';

type PostAcceptanceConversationCardProps = {
  dealershipName?: string | null;
  repName?: string | null;
  thread?: ContactMessageEntry[];
  unreadCount?: number;
  onOpen?: () => void;
};

const previewPrefix = (preview: MessagePreview, dealershipName: string | null | undefined) => {
  if (preview.senderRole === 'driver') return 'You:';
  if (preview.senderRole === 'platform') return `${PLATFORM_LABEL}:`;
  return `${dealershipName ?? 'Dealer'}:`;
};

const PostAcceptanceConversationCard = ({
  dealershipName,
  repName,
  thread,
  unreadCount = 0,
  onOpen,
}: PostAcceptanceConversationCardProps) => {
  const hasDealership = dealershipName != null && dealershipName !== '';
  const hasRep = repName != null && repName !== '';
  const preview = getMessagePreview(thread);

  return (
    <Box>
      <Text
        fontFamily="heading"
        fontSize="14px"
        fontWeight="700"
        color="leaseEndBlue"
        mx="2px"
        mb="9px"
      >
        Your conversation
      </Text>

      <MarketplaceCard p={2} overflow="hidden">
        <Flex
          align="center"
          gap="11px"
          px="16px"
          py="12px"
          borderBottomWidth="1px"
          borderColor="grayLightest"
        >
          <Center
            position="relative"
            w="38px"
            h="38px"
            borderRadius="full"
            bg="iceBlue"
            color="oceanBoatBlue"
            flexShrink={0}
          >
            <Icon as={FiMessageCircle} boxSize="16px" />
            <UnreadCountBubble count={unreadCount} position="absolute" top="-5px" insetEnd="-5px" />
          </Center>
          <Flex direction="column" minW={0}>
            <Text fontSize="14.5px" fontWeight="700" color="leaseEndBlue" noOfLines={1}>
              {hasDealership ? dealershipName : 'Your buyer'}
            </Text>
            {hasRep && (
              <Text fontSize="11.5px" color="grayMedium" noOfLines={1}>
                Your buyer · {repName}
              </Text>
            )}
          </Flex>
        </Flex>

        {onOpen == null ? (
          <Center px="16px" py="28px">
            <Text fontSize="13px" color="grayMedium" textAlign="center">
              Your conversation with the dealer will appear here.
            </Text>
          </Center>
        ) : (
          <Flex
            as="button"
            type="button"
            onClick={onOpen}
            align="center"
            gap="10px"
            px="16px"
            py="14px"
            textAlign="left"
            _hover={{ bg: 'blackAlpha.50' }}
          >
            {preview ? (
              <Text fontSize="13px" color="grayDark" noOfLines={1} flex="1" minW={0}>
                <Text as="span" fontWeight="700" color="leaseEndBlue">
                  {previewPrefix(preview, dealershipName)}
                </Text>{' '}
                {preview.text}
              </Text>
            ) : (
              <Text fontSize="13px" fontWeight="600" color="leaseEndBlue" flex="1" minW={0}>
                View your conversation
              </Text>
            )}
            <Box as="span" color="grayMedium" flexShrink={0}>
              <Icon as={FaChevronRight} boxSize="12px" />
            </Box>
          </Flex>
        )}
      </MarketplaceCard>
    </Box>
  );
};

export default PostAcceptanceConversationCard;
